import {
  Box,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EmployeeSelect from './Select/EmployeeSelect';
import RoleSelect from './Select/RoleSelect';
import AllocationTypeSelect from './Select/AllocationTypeSelect';
import DatePickerDialog from '../../common/DatePickerDialog';
import RequiredField from '../../common/RequiredField';
import ErrorSummary from 'components/common/ErrorSummary';
import { uniformizeDate } from 'common/utils';
import intl from 'components/i18n/ReactIntlWrapper';
import Spinner from 'components/Spinner';

function EmployeeProjectForm({
  handleEmployeeChange,
  employees,
  selectedEmployee,
  handleInputChange,
  handleRoleChange,
  selectedRole,
  roles,
  handleStartDate,
  handleDueDate,
  handleAddEmployeeProject,
  isEditMode,
  loadingSaveEmployeeProject,
  handleSaveEmployeeProject,
  handleCancelEdit,
  input,
  errors,
  disabled,
  handleAllocationTypeChange,
  selectedAllocationType,
  allocationTypes,
  setHiddenRole,
  setInput,
}) {
  const handleTimeSheetCheck = (e) => {
    setInput({ ...input, isTimesheetViewer: e.target.checked });
  };

  const handleEmptyRate = (e) => {
    handleInputChange(e);
  };

  const handleEmptyRateOnBlur = (e) => {
    const value = e.target.value === '' ? '0' : e.target.value;
    const eventCopy = { ...e };
    eventCopy.target.value = value;
    handleInputChange(eventCopy);
  };

  return (
    <Box component="form" noValidate>
      <Grid container spacing={2}>
        <Grid size={12}>
          <ErrorSummary errors={errors} showEverything={false} mode="warning" />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <EmployeeSelect
            handleEmployeeChange={handleEmployeeChange}
            employees={employees}
            selectedEmployee={selectedEmployee}
            disabled={disabled}
            newUser={false}
            id={null}
            securityRoles={null}
            edit={false}
            setHiddenRole={setHiddenRole}
            errors={errors}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            fullWidth
            label={intl.get('employeeProjectForm.hourlyRate')}
            placeholder={intl.get('employeeProjectForm.placeholder.hourlyRate')}
            name="hourlyRate"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            onBlur={handleEmptyRateOnBlur}
            value={input.hourlyRate ?? ''}
            onChange={handleEmptyRate}
            disabled={disabled}
            error={!!errors.hourlyRate}
            helperText={errors.hourlyRate}
            size="small"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            fullWidth
            label={intl.get('employeeProjectForm.monthlyRate')}
            placeholder={intl.get('employeeProjectForm.placeholder.monthlyRate')}
            name="monthlyRate"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            onBlur={handleEmptyRateOnBlur}
            value={input.monthlyRate ?? ''}
            onChange={handleEmptyRate}
            disabled={disabled}
            error={!!errors.monthlyRate}
            helperText={errors.monthlyRate}
            size="small"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={4}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <DatePickerDialog
            disabled={disabled}
            inputName="startDate"
            className="timesheet-datepicker"
            selected={disabled ? '' : input.startDate ? uniformizeDate(input.startDate) : ''}
            setSelected={handleStartDate}
            required
            error={!!errors.startDate}
            helperText={errors.startDate}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <DatePickerDialog
            disabled={disabled}
            inputName="dueDate"
            className="timesheet-datepicker"
            selected={disabled ? '' : input.dueDate ? uniformizeDate(input.dueDate) : ''}
            setSelected={handleDueDate}
            required
            error={!!errors.dueDate}
            helperText={errors.dueDate}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RoleSelect
            handleRoleChange={handleRoleChange}
            selectedRole={selectedRole}
            roles={roles}
            disabled={disabled}
            errors={errors}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={4}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <TextField
            fullWidth
            label={intl.get('employeeProjectForm.allocation')}
            placeholder={intl.get('employeeProjectForm.placeholder.allocation')}
            name="allocation"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={disabled ? 0 : input.allocation}
            onChange={handleInputChange}
            disabled={disabled}
            error={!!errors.allocation}
            helperText={errors.allocation}
            size="small"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <AllocationTypeSelect
            handleAllocationTypeChange={handleAllocationTypeChange}
            selectedAllocationType={selectedAllocationType}
            allocationTypes={allocationTypes}
            errors={errors}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 2,
          }}
        >
          <Box display="flex" flexDirection="column" justifyContent="flex-start">
            <FormControlLabel
              control={
                <Checkbox
                  checked={input.isTimesheetViewer}
                  onChange={handleTimeSheetCheck}
                  disabled={disabled}
                />
              }
              label={intl.get('employeeList.table.thead.timesheetViewer')}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={2} justifyContent="flex-start">
        {loadingSaveEmployeeProject ? <Spinner small /> :
          <>
          {isEditMode() ? (
            <>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 2,
                }}
              >
                <Button variant="outlined" color="inherit" fullWidth onClick={handleCancelEdit}>
                  {intl.get('projectDetail.cancel.button')}
                </Button>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSaveEmployeeProject}
                  disabled={Object.keys(errors).length > 0}
                  startIcon={<SaveIcon />}
                >
                  {intl.get('projectDetail.save.button')}
                </Button>
              </Grid>
            </>
          ) : (
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleAddEmployeeProject}
                disabled={Object.keys(errors).length > 0 || disabled}
              >
                {intl.get('projectDetail.add.button')}
              </Button>
            </Grid>
          )}
          </>
        }
      </Grid>
      <Box mt={2}>
        <RequiredField />
      </Box>
    </Box>
  );
}

export default EmployeeProjectForm;
